import { useEffect, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';

// Same-origin OTLP/HTTP path. In cluster, Emissary/Ambassador maps /otlp/* to
// the Coralogix agent OTLP HTTP receiver (:4318) — see app-state pos-frontend
// mapping (POS-2328). Browsers cannot use in-cluster NODE_IP/hostPort like
// backend SDKs (cb-pos, edge-manager); kiosk-interface uses the same URL shape
// with an on-device collector instead.
const OTLP_TRACES_URL = '/otlp/v1/traces';

/**
 * Cross-origin URLs that may receive W3C trace context headers (traceparent).
 *
 * Same-origin fetch/XHR already gets propagation without this list. This list
 * only gates *cross-origin* injection. A wildcard (/^.*$/) breaks third-party
 * SDKs whose CORS preflights do not allow traceparent (notably LaunchDarkly
 * evaluation/events). Restrict to owned backends that participate in our
 * distributed traces (cb-pos API, frontend-config, local dev).
 *
 * Do not include /otlp here — that is the collector export path, not a
 * distributed-trace hop, and same-origin /otlp does not need this option.
 */
export const TRACE_PROPAGATION_URL_PATTERNS = [
  // UAT/prod Citybase hosts (cb-pos-*, frontend-config, pos-frontend APIs, etc.)
  /^https?:\/\/([\w.-]+\.)?cityba\.se(?::\d+)?\//i,
  // Prod vanity / legacy Citybase hosts
  /^https?:\/\/([\w.-]+\.)?thecitybase\.com(?::\d+)?\//i,
  // Local cb-pos + frontend-configuration-service (see src/static/config.json)
  /^https?:\/\/localhost(?::\d+)?\//i,
  /^https?:\/\/127\.0\.0\.1(?::\d+)?\//i,
];

/**
 * LaunchDarkly flag key (same as cb_pos). Exact key — app uses
 * useCamelCaseFlagKeys: false. Default when missing/unavailable: false.
 */
export const OTEL_LD_FLAG = 'enable-otel-tracing';

/**
 * Initialization lifecycle for {@link initOpenTelemetry}.
 *
 * - not_started: never ran (or failed before provider.register()); safe to retry full init
 * - global_registered: provider.register() mutated global OTel API; do not construct/register again
 * - complete: register + instrumentations finished successfully
 *
 * provider.register() commits global tracer/context/propagator state. A later throw would leave
 * otelInitialized=false under a boolean guard and retries would rebuild a dead provider and
 * re-patch fetch. Tracking states avoids that ambiguity without blocking retries of pure
 * construction failures.
 */
const OTEL_INIT_STATE = {
  NOT_STARTED: 'not_started',
  GLOBAL_REGISTERED: 'global_registered',
  COMPLETE: 'complete',
};

let otelInitState = OTEL_INIT_STATE.NOT_STARTED;

/**
 * Initializes OpenTelemetry browser instrumentation.
 *
 * - Patches global fetch() and XMLHttpRequest to generate trace spans
 * - Exports traces via OTLP/HTTP to the same-origin /otlp proxy
 * - Idempotent once global registration has run (complete or partial)
 * - Failures are swallowed so the cashiering UI still boots
 *
 * Call only after LaunchDarkly has evaluated {@link OTEL_LD_FLAG} as true
 * (see {@link OpenTelemetryGate}). Live flag toggles require a hard refresh;
 * this does not tear down instrumentation when the flag turns off mid-session.
 */
export function initOpenTelemetry() {
  // Already finished, or register() already took the global OTel slot — do not retry.
  if (
    otelInitState === OTEL_INIT_STATE.COMPLETE ||
    otelInitState === OTEL_INIT_STATE.GLOBAL_REGISTERED
  ) {
    return;
  }

  try {
    const resource = new Resource({
      // Keep service.name stable for Coralogix filters / emissary drop allowlist.
      'service.name': 'pos-frontend-traces',
      // Build-time SHA (Docker ARG/ENV COMMIT_SHA → Parcel process.env). Matches
      // kiosk-interface. Local/dev without ARG is 'unknown'. Searchable on spans
      // as resource service.version (see Coralogix process/resource tags).
      'service.version':
        (typeof process !== 'undefined' &&
          process.env &&
          process.env.COMMIT_SHA) ||
        'unknown',
    });

    const exporter = new OTLPTraceExporter({
      url: OTLP_TRACES_URL,
    });

    const provider = new WebTracerProvider({
      resource,
    });

    provider.addSpanProcessor(new BatchSpanProcessor(exporter));

    // Global mutation boundary: after this, retries must not call register() again.
    provider.register();
    otelInitState = OTEL_INIT_STATE.GLOBAL_REGISTERED;

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URL_PATTERNS,
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URL_PATTERNS,
        }),
      ],
    });

    otelInitState = OTEL_INIT_STATE.COMPLETE;
    // No success console.log — avoid kiosk/prod DevTools noise. Spans in
    // Coralogix are the signal that init worked.
  } catch (error) {
    // not_started → allow a later full retry (e.g. construction failed before register).
    // global_registered → leave as-is so we never double-register the global API.
    console.error(
      '[OpenTelemetry] Failed to initialize instrumentation:',
      error,
    );
  }
}

/**
 * Blocks app mount until LaunchDarkly is ready, then optionally starts OTel
 * before children (and their HTTP traffic) render. Must sit under LDProvider.
 *
 * When enable-otel-tracing is on, init runs before children so early HTTP is
 * instrumented. Live flag toggles need a hard refresh; this gate does not tear
 * down instrumentation if the flag turns off mid-session.
 *
 * Status chatter (init/skip/LD reason) is intentionally not console.log'd so
 * cashiers never see it; only real failures warn/error.
 */
export function OpenTelemetryGate({ children }) {
  const ldClient = useLDClient();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    let enabled = false;
    try {
      // variationDetail (rather than plain variation) also returns the
      // evaluation reason (e.g. RULE_MATCH, FALLTHROUGH, OFF, or ERROR).
      const detail = ldClient.variationDetail(OTEL_LD_FLAG, false);
      enabled = Boolean(detail.value);
    } catch (error) {
      console.warn(
        `[OpenTelemetry] LD flag '${OTEL_LD_FLAG}' evaluation failed; skipping instrumentation`,
        error,
      );
      enabled = false;
    }

    if (enabled) {
      initOpenTelemetry();
    }

    setReady(true);
  }, [ldClient]);

  if (!ready) {
    return null;
  }

  return children;
}
